// ...imports (unchanged)
import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import './css/Explore.css';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { countries } from 'countries-list';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { enUS } from 'date-fns/locale';
import emailjs from '@emailjs/browser';
import { Toaster, toast } from 'react-hot-toast';
import { db, collection, addDoc } from '../firebase';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import locations from '../data/trips';
import ReCAPTCHA from 'react-google-recaptcha';
import { format } from 'date-fns';

function DestinationPage() {
  const { name: destination } = useParams();
  const place = locations.find((loc) => loc.name === destination);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
    setError,
    clearErrors,
  } = useForm({ mode: 'onChange' });

  const [userLocation, setUserLocation] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [childrenCount, setChildrenCount] = useState(0);
  const [childrenAges, setChildrenAges] = useState([]);
  const [currency, setCurrency] = useState('KES');
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [dateRange, setDateRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(new Date().setDate(new Date().getDate() + 1)),
      key: 'selection',
    },
  ]);

  // --- Geolocation ---
  useEffect(() => {
    fetch('https://ipapi.co/json')
      .then((res) => res.json())
      .then((data) => setUserLocation(data))
      .catch(() => setUserLocation({ country_code: 'KE' }));
  }, []);

  // --- Validate children ages ---
  const validateChildrenAges = () => {
    const invalid = childrenAges.some((age) => {
      const num = parseInt(age, 10);
      return isNaN(num) || num >= 18 || num <= 0;
    });
    if (invalid) {
      toast.error('All children must be below 18 years old.');
      return false;
    }
    return true;
  };

  // --- Form Submission ---
  const onSubmit = async (data) => {
    if (!recaptchaToken) {
      toast.error('Please complete the reCAPTCHA.');
      return;
    }

    if (!validateChildrenAges()) return;

    try {
      const doc = {
        ...data,
        phone: data.phone,
        childrenAges: childrenAges.join(', '),
        budget: `${data.budgetAmount} (${currency})`,
        tripId: place?.id,
        tripName: place?.name,
        submittedAt: new Date().toISOString(),
        detectedLocation: userLocation,
        checkInDate: format(dateRange[0].startDate, 'yyyy-MM-dd'),
        checkOutDate: format(dateRange[0].endDate, 'yyyy-MM-dd'),
      };

      await toast.promise(
        emailjs.send('service_z5z38vy', 'template_l69rocw', doc, 'iOQXap89SbtigOxLR'),
        { loading: 'Sending...', success: 'Sent!', error: 'Send failed' }
      );

      await addDoc(collection(db, 'bookings'), doc);

      toast.success('Form submitted successfully!');
      reset();
      setChildrenCount(0);
      setChildrenAges([]);
      setRecaptchaToken(null);
      setPhoneNumber('');
    } catch (error) {
      console.error(error);
      toast.error('Submission failed. Try again later.');
    }
  };

  return (
    <>
      <SEO
        title={`Luxury ${place?.name} Safari Packages | Joujo Safaris`}
        description={`Plan your dream safari in ${place?.name} with Joujo Safaris. Enjoy wildlife adventures, cultural experiences, and luxury lodges tailored to your needs.`}
        url={`https://joujosafaris.com/explore/${place?.name}`}
        image={place?.image}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'TouristTrip',
          name: `Luxury ${place?.name} Safari`,
          description: `Unforgettable safari packages in ${place?.name} including Big Five tours, adventure activities, and luxury lodge stays.`,
          image: place?.image,
          provider: {
            '@type': 'TravelAgency',
            name: 'Joujo Safaris',
            url: 'https://joujosafaris.com',
          },
        }}
      />
      <Navbar />

      <div className="about-main-title container" style={{ marginTop: '100px', marginBottom: '20px' }}>
        <h1 className="text-color">Explore {place?.name}</h1>
        <h2>Safari Packages & Tailored Journeys</h2>
        <p>{place?.desc}</p>
      </div>

      <div className="container">
        <div style={{ marginTop: '70px' }} className="dp-container">
          <div className="dp-header">
            <h2>Get a Free Travel Quote for {place?.name}</h2>
            <p>Customize your safari in {place?.name} with Joujo Safaris. Fill in your details below and our team will get back to you with the best options.</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="dp-form-group">
            <label>Full Name (as per passport)*</label>
            <input {...register('fullName', { required: 'Please enter your full name', minLength: 3 })} />
            {errors.fullName && <span className="dp-error">{errors.fullName.message}</span>}

            <label>Email Address*</label>
            <input type="email" {...register('email', { required: 'Email is required' })} />
            {errors.email && <span className="dp-error">{errors.email.message}</span>}

            <label>City of Residence*</label>
            <input {...register('city', { required: 'City is required' })} />
            {errors.city && <span className="dp-error">{errors.city.message}</span>}

            <label>Nationality (for visa & travel purposes)*</label>
            <select {...register('nationality', { required: 'Nationality is required' })}>
              <option value="">Select</option>
              {Object.entries(countries).map(([code, c]) => (
                <option key={code} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
            {errors.nationality && <span className="dp-error">{errors.nationality.message}</span>}

            <label>Phone Number (with country code)*</label>
            <PhoneInput
              country={userLocation?.country_code?.toLowerCase() || 'ke'}
              value={phoneNumber}
              onChange={(value) => {
                setPhoneNumber(value);
                setValue('phone', '+' + value);
              }}
            />
            <input
              type="hidden"
              {...register('phone', {
                required: 'Phone number is required',
              })}
            />
            {errors.phone && <span className="dp-error">{errors.phone.message}</span>}

            <label>Destination*</label>
            <input value={place?.name} {...register('arrivalCity', { required: 'Destination is required' })} readOnly />
            {errors.arrivalCity && <span className="dp-error">{errors.arrivalCity.message}</span>}

            <label>Travel Dates (Check-in / Check-out)</label>
            <DateRange
              editableDateInputs={true}
              onChange={(item) => setDateRange([item.selection])}
              moveRangeOnFirstSelection={false}
              ranges={dateRange}
              locale={enUS}
            />

            <div className="dp-flex">
              <div className="dp-a">
                <label>Adults*</label>
                <input type="number" {...register('adults', { required: 'At least 1 adult required', min: 1 })} />
                {errors.adults && <span className="dp-error">{errors.adults.message}</span>}
              </div>

              <div className="dp-b">
                <label>Children</label>
                <input
                  type="number"
                  {...register('children', { min: 0 })}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    setChildrenCount(val);
                    setChildrenAges(Array(val).fill(''));
                  }}
                />
              </div>
            </div>

            {childrenCount > 0 && (
              <div className="dp-children-ages">
                <label>Children Ages*</label>
                {childrenAges.map((age, idx) => (
                  <div key={idx}>
                    <input
                      type="number"
                      placeholder={`Child ${idx + 1} Age`}
                      value={age}
                      onChange={(e) => {
                        const val = e.target.value;
                        const updatedAges = [...childrenAges];
                        updatedAges[idx] = val;
                        setChildrenAges(updatedAges);

                        const ageNum = parseInt(val);
                        if (!val || isNaN(ageNum) || ageNum <= 0 || ageNum >= 18) {
                          setError(`childAge_${idx}`, {
                            type: 'manual',
                            message: 'Age must be between 1 and 17',
                          });
                        } else {
                          clearErrors(`childAge_${idx}`);
                        }
                      }}
                      required
                    />
                    {errors[`childAge_${idx}`] && <span className="dp-error">{errors[`childAge_${idx}`].message}</span>}
                  </div>
                ))}
              </div>
            )}

            <label>Estimated Budget*</label>
            <div className="dp-budget-row">
              <input
                type="number"
                {...register('budgetAmount', {
                  required: 'Budget is required',
                  min: {
                    value: 2000,
                    message: 'Minimum budget is 2000',
                  },
                })}
              />
              <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
                <option>KES</option>
                <option>USD</option>
                <option>EUR</option>
              </select>
            </div>
            {errors.budgetAmount && <span className="dp-error">{errors.budgetAmount.message}</span>}

            <label>Additional Message (special requests, travel preferences)</label>
            <textarea {...register('message')} />

            <div className="dp-captcha">
              <label>reCAPTCHA Verification*</label>
              <ReCAPTCHA
                sitekey="6LcaXrwrAAAAAGm7Q3lJbS2g18A-bZ9RvCF7yTo3"
                onChange={(token) => setRecaptchaToken(token)}
                onExpired={() => setRecaptchaToken(null)}
              />
            </div>

            <button type="submit" className="dp-submit-btn">
              Submit
            </button>
          </form>
        </div>
      </div>

      <Toaster position="top-right" />
      <Footer />
    </>
  );
}

export default DestinationPage;
